import { useEffect, useRef, useState } from "react";
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from "recoil";
import { Form, FormikProvider, useFormik } from "formik";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import closeFill from "@iconify/icons-eva/close-fill";
// material
import { Alert, Divider, Stack, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import MIconButton from "@components/button/MIconButton";
import useIsMountedRef from "@hooks/useIsMountedRef";
import axios from "@utils/axios";
import { emailField } from "@utils/emailRule";
import { publicSettingsQuery } from "@states/publicSettings";
import IdentityFields, {
  EMPTY_IDENTITY,
  IdentityValues,
  ResetIdentityButton,
} from "./IdentityFields";
import { failureMessage } from "./failureMessage";

/** 서버가 문장을 못 돌려준 경우에만 쓴다. 평소에는 서버가 준 것을 그대로 보여준다. */
const ACCEPTED_FALLBACK = "신청이 접수되었습니다. 승인되면 메일로 알려드립니다.";

/**
 * 어떤 실패든 이 한 문장만 보여준다.
 *
 * 422 본문의 `detail` 은 **문자열이 아니라 객체 배열**이다. 실측:
 *   {"detail":[{"type":"missing","loc":["body","name"],"msg":"Field required", ...}]}
 * 옆 폼들처럼 `error.detail === "..."` 로 비교하면 영원히 안 맞고, 그렇다고 그 값을
 * 그대로 `<Alert>` 에 넣으면 React 가 "Objects are not valid as a React child" 로 터진다.
 * 사용자가 고칠 수 있는 입력 문제는 아래 Yup 이 서버와 같은 조건으로 먼저 잡으므로,
 * 여기까지 온 오류는 사용자가 손댈 수 있는 게 아니다.
 */
const SUBMIT_FAILED = "신청을 접수하지 못했습니다. 잠시 후 다시 시도해주세요.";

type Identity = { txSeqNo: string; telNo: string; otpNo: string };

const requestSignup = async (name: string, email: string, identity: Identity | null) => {
  // 끝에 슬래시를 붙이면 307 로 한 번 더 돈다 (실측). 붙이지 않는다.
  const response = await axios({
    method: "post",
    url: "signup-requests",
    // 인증을 안 받았으면 **아예 안 보낸다.** 빈 문자열을 보내면 서버가 «셋 중
    // 하나만 왔다» 로 읽는데, 그건 부분적으로 채워 검증을 건너뛰려는 시도와
    // 구분되지 않는다.
    data: identity ? { name, email, ...identity } : { name, email },
  });

  return response.data;
};

/** 인증번호 발송. 아직 계정이 없어 토큰으로 막을 수 없는 문이라 횟수 제한만 있다. */
const sendSignupOtp = async (values: IdentityValues, agreedTerms: string[]) => {
  const response = await axios({
    method: "post",
    url: "signup-requests/identity",
    data: {
      name: values.name.trim(),
      regFront: values.regFront,
      regRear: values.regRear,
      telComCd: values.telComCd,
      telNo: values.telNo.replace(/\D/g, ""),
      // 서버가 다시 본다. 이유는 `IdentityFields` 의 `onRequestOtp` 주석에.
      agreedTerms,
    },
  });
  return response.data.txSeqNo as string;
};

export default function SignupRequestForm({ toggleForm }: { toggleForm: () => void }) {
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // 게이트가 꺼져 있으면 인증 칸을 **아예 그리지 않는다.** 필요 없는 절차를
  // 보여주면 그것만으로 가입을 포기하는 사람이 생긴다.
  const publicSettings = useRecoilValueLoadable(publicSettingsQuery);
  // 실패할 때마다 다시 읽는다. 화면을 연 뒤에 게이트가 바뀌면 이 화면은 그걸
  // 모른다 — 이유는 `LoginForm` 의 같은 자리에 적었다(2026-09-23 수동 TC GL-09).
  const refreshSettings = useRecoilRefresher_UNSTABLE(publicSettingsQuery);
  // 다시 읽는 동안은 마지막으로 알던 값을 쓴다. 그 사이 인증칸이 사라지면 동의
  // 체크가 풀린다.
  const lastKnown = useRef(false);
  if (publicSettings.state === "hasValue") {
    lastKnown.current = publicSettings.contents.identityOnSignup;
  }
  const needIdentity = lastKnown.current;

  const [identity, setIdentity] = useState<IdentityValues>(EMPTY_IDENTITY);
  const [txSeqNo, setTxSeqNo] = useState("");
  const [sending, setSending] = useState(false);
  const [otpError, setOtpError] = useState("");

  const sendOtp = async (agreedTerms: string[]) => {
    setOtpError("");
    setSending(true);
    try {
      // 이름은 신청 폼의 값을 쓴다. 인증 이름과 신청 이름이 다르면 승인한 사람이
      // «누구지» 를 겪는다.
      setTxSeqNo(await sendSignupOtp({ ...identity, name: formik.values.name }, agreedTerms));
      enqueueSnackbar("인증번호를 보냈습니다.", { variant: "success" });
    } catch (error: any) {
      setOtpError(
        failureMessage(error, {
          fallback: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
          identityRequired: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
        }),
      );
      refreshSettings();
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  // 36 · 320 은 서버 `SignupRequestCreateDTO` 와 같은 값이다. 여기서 먼저 막지 않으면
  // 서버가 422 를 주는데, 그 본문은 위 주석 이유로 사용자에게 보여줄 수가 없다.
  const SignupRequestSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .max(36, "이름은 36자까지 입력할 수 있습니다.")
      .required("이름을 입력해주세요."),
    // 서버와 같은 조건 — `utils/emailRule.ts`(f1-4). Yup `.email()` 만으로는 `gumin@naver` 가 통과했다.
    email: emailField("올바른 이메일을 입력해주세요.").max(320, "이메일이 너무 깁니다."),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      afterSubmit: ""
    },
    validationSchema: SignupRequestSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        const accepted = await requestSignup(
          values.name.trim(),
          values.email.trim(),
          needIdentity && txSeqNo
            ? { txSeqNo, telNo: identity.telNo.replace(/\D/g, ""), otpNo: identity.otpNo }
            : null,
        );

        // 서버는 **이미 가입된 이메일이든 처음 보는 이메일이든 똑같은 202 를 준다.**
        // 응답이 갈리면 그것만으로 가입자 명단을 긁을 수 있기 때문이다. 그러니 화면도
        // 무엇이 일어났는지 추측하지 않고 서버가 준 문장 하나만 보여준다.
        enqueueSnackbar(accepted?.message || ACCEPTED_FALLBACK, {
          variant: "success",
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill}/>
            </MIconButton>
          ),
        });

        if (isMountedRef.current) {
          setSubmitting(false);
        }

        toggleForm();

      } catch (error: any) {
        // 옆 폼들과 달리 `resetForm()` 을 부르지 않는다. 필드가 둘인데다 비밀번호처럼
        // 지워야 할 값도 아니라서, 네트워크가 한 번 흔들렸다고 다시 타이핑하게 둘 이유가 없다.
        //
        // 서버가 준 이유는 **그대로** 보여 준다. 예전에는 무엇이든 «잠시 후 다시» 여서
        // 인증번호를 틀린 사람이 기다렸다가 같은 번호로 또 눌렀다(수동 TC S-07).
        // 이 문은 계정을 응답 뒤에 조회하므로 여기 오는 이유는 가입 여부와 상관이
        // 없다 — 근거는 `failureMessage` 머리글에.
        if (isMountedRef.current) {
          setSubmitting(false);
          setErrors({
            afterSubmit: failureMessage(error, {
              fallback: SUBMIT_FAILED,
              identityRequired:
                "회원가입 신청에 본인인증이 필요합니다. 본인인증을 마친 뒤 다시 눌러주세요. " +
                "본인인증 칸이 보이지 않으면 화면을 새로고침해주세요.",
            }),
          });
        }
        refreshSettings();
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  // 게이트가 켜져 있다가 꺼지면 인증의 흔적을 지운다. 이유는 `LoginForm` 의 같은 자리에.
  const wasIdentityOn = useRef(needIdentity);
  useEffect(() => {
    if (wasIdentityOn.current && !needIdentity) {
      setTxSeqNo("");
      setOtpError("");
      setIdentity(EMPTY_IDENTITY);
      formik.setErrors({});
    }
    wasIdentityOn.current = needIdentity;
    // `formik` 은 렌더마다 새 객체라 넣으면 매번 돈다. 보는 것은 게이트 값 하나다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needIdentity]);

  /**
   * 신청 버튼을 눌러도 되는가.
   *
   * 예전에는 `disabled` 가 **아예 없었다.** 화면에 들어오자마자, 한 글자도 안 쳤는데
   * 눌리는 상태였다. 누르면 서버가 422 로 거절하는데 그 본문은 사용자에게 보여줄 수
   * 없어서(열거 방어) «실패했습니다» 만 뜬다 — 무엇이 모자란지 알 길이 없다.
   *
   * 셋을 본다:
   *
   *     dirty · isValid   이름과 이메일이 규칙에 맞는가 (`dirty` 가 없으면 빈 폼도
   *                       «오류 없음» 이라 통과한다 — Formik 의 `isValid` 는 처음에 참이다)
   *     txSeqNo           인증번호를 **받았는가**. 이 값은 동의를 마쳐야 생긴다 —
   *                       발송 버튼이 필수 약관에 잠겨 있기 때문이다
   *     otpNo             받은 번호를 **넣었는가**
   *
   * 본인인증 게이트가 꺼져 있으면 뒤 둘은 보지 않는다. 그때는 입력칸 자체가 없다.
   *
   * ★ `isValid` 를 **쓰지 않는다.** 서버 거절 문구(`afterSubmit`)도 Formik 의 오류
   * 칸에 들어가서, 한 번 실패하면 `isValid` 가 거짓이 된다. 그러면 인증번호를 틀린
   * 사람이 번호를 고쳐도 버튼이 잠긴 채 «이름과 이메일을 입력해주세요» 가 떴다 —
   * 이름·이메일을 다시 쳐야 풀렸다(2026-09-23 화면 검증에서 찾았다). 보는 것은
   * **두 칸의 오류**뿐이다.
   */
  const fieldsValid = !formik.errors.name && !formik.errors.email;
  const canSubmit =
    formik.dirty &&
    fieldsValid &&
    (!needIdentity || (Boolean(txSeqNo) && identity.otpNo.length >= 4));

  return (
    <FormikProvider value={formik}>
      {/* 로그인 폼과 같은 이유로 `autoComplete="off"` 를 쓰지 않는다. 이름과
          이메일을 손으로 다시 치게 할 이유가 없다. */}
      <Form noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="name"
            type="text"
            label="이름"
            {...getFieldProps("name")}
            // ★ 인증번호를 받은 뒤에는 **잠근다.** 이 이름으로 본인인증을 했으므로
            // 바꾸면 신청 이름과 인증 이름이 갈라진다. 예전에는 고칠 수 있었고, 서버도
            // 안 봐서 «김철수» 로 신청하고 «홍길동» 으로 인증한 계정이 승인될 수
            // 있었다(2026-09-23 수동 TC S-11). 서버도 이제 막지만, 거기까지 가면 받은
            // 인증번호를 버리게 되므로 여기서 먼저 막는다.
            disabled={needIdentity && Boolean(txSeqNo)}
            error={Boolean(touched.name && errors.name)}
            helperText={
              (touched.name && errors.name) ||
              (needIdentity && txSeqNo
                ? "본인인증에 쓴 이름입니다. 바꾸려면 아래 «정보를 다시 입력하기» 를 눌러주세요."
                : undefined)
            }
          />
          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            label="이메일"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />
          {needIdentity && (
            <>
              <Divider textAlign="left">
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  본인인증
                </Typography>
              </Divider>
              {/* 로그인 화면과 **같은 묶음**을 쓴다. 각자 그리면 한쪽만 고쳤을 때
                  조용히 갈라진다 — 통신사 목록을 한쪽에만 추가하는 식이다. */}
              <IdentityFields
                values={{ ...identity, name: formik.values.name }}
                onChange={(next) => setIdentity(next)}
                sent={Boolean(txSeqNo)}
                sending={sending}
                onRequestOtp={sendOtp}
                error={otpError}
                disabled={isSubmitting}
                hideName
              />
              {/* 이름을 잠갔으므로 **풀 길**을 둔다. 받은 인증번호를 버리고 처음부터
                  다시 받는다 — 이름이 바뀌면 인증도 새로 해야 하기 때문이다. */}
              {txSeqNo && !isSubmitting && (
                <ResetIdentityButton
                  onReset={() => {
                    setTxSeqNo("");
                    setIdentity({ ...identity, otpNo: "" });
                    setOtpError("");
                  }}
                />
              )}
            </>
          )}
          {errors.afterSubmit && (
            <Alert severity="error">{errors.afterSubmit}</Alert>
          )}
        </Stack>
        <Stack sx={{ my: 2 }}/>
        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={!canSubmit}
        >
          회원가입 신청하기
        </LoadingButton>
        {/* 왜 못 누르는지 **버튼 바로 아래에서** 말한다. 잠긴 이유가 둘이라
            («칸이 덜 찼다» · «인증이 남았다») 한 문장만 띄우면 다 채운 사람이
            이유를 못 찾는다. */}
        {!canSubmit && !isSubmitting && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 1 }}
          >
            {formik.dirty && fieldsValid
              ? "본인인증을 마치면 신청할 수 있습니다."
              : "이름과 이메일을 입력해주세요."}
          </Typography>
        )}

        <Stack
          justifyContent="space-between"
          sx={{ my: 3 }}
          spacing={3}
        >
          <Divider />
          <Typography
            align="left"
            onClick={() => toggleForm && toggleForm()}
            variant="subtitle2"
            sx={{ color: "text.disabled", textDecoration: "underline", cursor: "pointer" }}
            noWrap={true}
          >
            다시 로그인하기
          </Typography>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
